import { TRPCClientErrorBase } from "@trpc/client";
import { TRPCDefaultErrorShape } from "@trpc/server";
import { FormEvent, JSX, PropsWithChildren, ReactElement, useEffect, useRef } from "react";

import { type Feedback, LocalFeedback } from "~/components/feedback";
import { Button, Flex, LoadingOverlay, Tooltip, type UseFormReturnType } from "~/ui";

export type FormFooterProps = {
  readonly submitText?: string;
  readonly cancelText?: string;
  readonly submitting?: boolean;
  /** True while useFileUpload startAllUploads is uploading files before the save mutation runs. */
  readonly uploading?: boolean;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly onCancel?: () => void;
  readonly canceling?: boolean;
  readonly cancelDisabled?: boolean;
  readonly cancelHelp?: string;
  readonly otherActions?: JSX.Element;
  readonly submitButtonOverride?: JSX.Element;
  readonly footerRight?: JSX.Element;
};
export const FormFooter = (props: FormFooterProps) => {
  let cancelButton = null;
  if (props.onCancel || props.cancelText) {
    cancelButton = (
      <Button variant="subtle" onClick={props.onCancel} loading={props.canceling} disabled={props.cancelDisabled}>
        {props.cancelText || "Cancel"}
      </Button>
    );
  }
  if (cancelButton && props.cancelHelp) {
    cancelButton = (
      <Tooltip label={props.cancelHelp} position="top">
        {cancelButton}
      </Tooltip>
    );
  }

  return (
    <Flex direction="row">
      {props.footerRight && (
        <Flex direction="row" align="center" w="100%" justify="left" mt="md" gap="md">
          {props.footerRight}
        </Flex>
      )}
      <Flex direction="row" align="center" w="100%" justify="right" mt="md" gap="md">
        {cancelButton}
        {props.otherActions}
        {props.submitButtonOverride ?? (
          <Button
            loading={props.submitting || props.uploading}
            type="submit"
            disabled={props.loading || props.submitting || props.uploading || props.disabled}
          >
            {props.submitText || "Save"}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export type FormProps<T extends Record<string, unknown>> = FormFooterProps &
  PropsWithChildren<{
    readonly form: UseFormReturnType<T>;
    readonly feedback?: Feedback;
    readonly mutationError?: TRPCClientErrorBase<TRPCDefaultErrorShape> | null;
    readonly fieldGap?: string | number;
    readonly onSubmit: (data: T) => void;
    readonly onSubmitNative?: (event: FormEvent<HTMLFormElement>) => void;
    readonly footerAsHeader?: boolean;
  }>;

export const Form = <T extends Record<string, unknown>>({
  feedback,
  mutationError,
  fieldGap = "sm",
  footerAsHeader,
  ...props
}: FormProps<T>): ReactElement => {
  const formFeedback =
    mutationError != null && mutationError.data?.code === "BAD_REQUEST"
      ? [
          {
            message: mutationError.message,
            visible: true,
          },
        ]
      : feedback;

  const ref = useRef<HTMLFormElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const hasUserInteractedRef = useRef(false);
  const submitReentryRef = useRef(false);
  const submitInFlight = props.submitting === true || props.uploading === true;

  // Dispatch scformchange when Mantine form dirty state changes (catches Select, DatePicker, etc.)
  // Gated behind user interaction so programmatic setValues (e.g. async hydration) won't
  // mark modals dirty before the user has touched anything.
  const isDirty = props.form.isDirty();
  useEffect(() => {
    if (isDirty && hasUserInteractedRef.current) {
      ref.current?.dispatchEvent(new Event("scformchange", { bubbles: true }));
    }
  }, [isDirty]);

  return (
    <form
      ref={ref}
      onPointerDownCapture={() => {
        hasUserInteractedRef.current = true;
      }}
      onKeyDownCapture={() => {
        hasUserInteractedRef.current = true;
      }}
      onSubmit={event => {
        event.stopPropagation();
        if (submitReentryRef.current || submitInFlight || props.loading === true || props.disabled === true) {
          event.preventDefault();
          return;
        }
        props.form.onSubmit(
          data => {
            submitReentryRef.current = true;
            try {
              props.onSubmit(data);
            } finally {
              // Always release after the sync callback. A sticky lock would freeze Save on
              // callers that return without starting a mutation (e.g. UpdatePriceForm field errors)
              // while submitting stays false. In-flight clicks are blocked by submitInFlight.
              submitReentryRef.current = false;
            }
          },
          () => {
            // On validation failure, scroll to show errors and buttons
            setTimeout(() => {
              if (footerRef.current) {
                footerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
              }
            }, 100);
          },
        )(event);
        props.onSubmitNative?.(event);
      }}
      onReset={e => props.form.onReset(e)}
      onChange={() => ref.current?.dispatchEvent(new Event("scformchange", { bubbles: true }))}
    >
      <LoadingOverlay visible={props.loading === true} />
      {footerAsHeader === true && <FormFooter {...props} />}
      <Flex direction="column" gap={fieldGap}>
        {props.children}
      </Flex>
      <div ref={footerRef}>
        <LocalFeedback mt="sm" feedback={formFeedback} />
        {footerAsHeader !== true && <FormFooter {...props} />}
      </div>
    </form>
  );
};

export type GetInputPropsOverrides<FieldType> = {
  onChange?: (value: FieldType) => unknown;
  onFocus?: () => unknown;
  onBlur?: () => unknown;
};
export function formInputPropsWithOverride<FormType, FieldType>(
  form: UseFormReturnType<FormType>,
  path: string,
  { onChange, onFocus, onBlur }: GetInputPropsOverrides<FieldType>,
): ReturnType<UseFormReturnType<FormType>["getInputProps"]> {
  const props = form.getInputProps(path);
  const skipExisting = (value: unknown) => typeof value == "boolean" && !value;
  const existingKeys = Object.keys(props);
  const existingOnChange = existingKeys.includes("onChange") ? props.onChange : undefined;
  const existingOnFocus = existingKeys.includes("onFocus") ? props.onFocus : undefined;
  const existingOnBlur = existingKeys.includes("onBlur") ? props.onBlur : undefined;

  if (onChange != undefined) {
    props.onChange = (value: FieldType) => {
      const ret = onChange(value);
      if (!skipExisting(ret)) {
        existingOnChange?.(value);
      }
    };
  }

  if (onFocus != undefined) {
    props.onFocus = () => {
      const ret = onFocus();
      if (!skipExisting(ret)) {
        existingOnFocus?.();
      }
    };
  }

  if (onBlur != undefined) {
    props.onBlur = () => {
      const ret = onBlur();
      if (!skipExisting(ret)) {
        existingOnBlur?.();
      }
    };
  }

  return props;
}
